import "./modelstyle.scss";

import {
  AssociateDatasetButton,
  ModelAddNewVersionButton,
  ModelDeployToggleButton,
  ModelDetailsButton,
  ModelInferModalButton,
  ModelTrainButton,
  ScoreModelParamsLimit,
} from "./ModelButtons";
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CCol,
  CCollapse,
  CContainer,
  CDataTable,
  CForm,
  CFormGroup,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CRow,
  CSwitch,
  CTextarea,
} from "@coreui/react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import {
  useAssociateModelVersionInputFieldsWithModelVersionDataset,
  useAssociateNewModelDataset,
  useAssociateNewModelVersionDataset,
  useAssociatedModelVersionInputFieldsWithSourceDataRecordFields,
  useDeleteModelVersionDatasetAssociation,
  useGetModel,
  useGetModelVersion,
  useGetModels,
  useInferFromModel,
  useModelDatasets,
  useModelVersionDataset,
  useModelVersionInputFields,
} from "./ModelService";

import CIcon from "@coreui/icons-react";
import { Can } from "src/helpers/Auth";
import { CreateItemButton } from "../forms/FormUtils";
import { CreateModelModal } from "../forms/ModelModals";
import { DatasetListSelect } from "../datasets/Datasets";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { ListSelect } from "src/reusable";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import { ModelDatasets } from "./ModelDetail";
import { ModelVersionConfig } from "./ModelConfig";
import { ModifyModelVersionModal } from "../forms/ModelVersionModals";
import { ProjectAIProductID } from "src/reusable/products";
import { PropTypes } from "prop-types";
import ReactQueryLoading from "src/reusable/ReactQueryLoading";
import { Table } from "react-bootstrap";
import TextareaAutosize from "react-textarea-autosize";
import VersionLogsButton from "../buttons/functional-buttons/VersionLogsButton";
import { determineLoginPlatformType } from "src/reusable/Functions";
import { freeSet } from "@coreui/icons";
import { useDatasetFields } from "../datasets/services/DatasetServices";
import { useQueryClient } from "react-query";
import { PageTopHelpButton } from "src/components/help";

const ModelList = () => {
  const [isViewDeployedAll, setIsViewDeployedAll] = useState(false);
  const [modelsView, setModelsView] = useState("all");

  const {
    data: modelList,
    isLoading: isModelListLoading,
    status: modelListStatus,
    error: modelListError,
  } = useGetModels(null);

  const handleModelViewChange = (event, modelView) => {
    if (!modelView) {
      return;
    }
    setModelsView(modelView);
  };

  useEffect(() => {
    if (modelsView === "deployed") {
      setIsViewDeployedAll(true);
    } else {
      setIsViewDeployedAll(false);
    }
  }, [modelsView]);

  return (
    <CContainer
      fluid
      style={{ padding: "0 25px 0 25px", marginTop: "1%" }}
      data-testid="mdl-page"
    >
      <CRow style={{ marginBottom: "1em" }}>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "2em",
          }}
        >
          <div>
            <h1 data-testid="mdl-title">{`${determineLoginPlatformType()} Model Control`}</h1>
            {`Control Panel Summaries for all ${determineLoginPlatformType()} Models and Model Versions.`}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1em",
            }}
          >
            <PageTopHelpButton productLabel="Models" product="models" />
            <ToggleButtonGroup
              value={modelsView}
              exclusive
              size="small"
              onChange={handleModelViewChange}
              sx={{ backgroundColor: "background.paper" }}
            >
              <ToggleButton
                data-testid="mdl-btn-filter-all"
                data-attr="models:toggle:filter-all-models"
                value="all"
                color="primary"
                sx={{
                  "&.Mui-selected": {
                    color: "#0088b2",
                    backgroundColor: "#79d1ec2a",
                  },
                }}
              >
                All
              </ToggleButton>
              <ToggleButton
                data-testid="mdl-btn-filter-deployed"
                data-attr="models:toggle:filter-deployed-models"
                value="deployed"
                color="primary"
                sx={{
                  "&.Mui-selected": {
                    color: "#0088b2",
                    backgroundColor: "#79d1ec2a",
                  },
                }}
              >
                Deployed
              </ToggleButton>
            </ToggleButtonGroup>

            <div>
              <CreateItemButton
                data-testid="mdl-btn-model"
                data-attr="models:btn:create-model"
                itemName="Model"
                createItemModal={CreateModelModal}
              />
            </div>
          </div>
        </Box>
      </CRow>

      <CRow data-testid="mdl-grid">
        <ReactQueryLoading
          isLoading={isModelListLoading}
          status={modelListStatus}
          error={modelListError}
          loadingHeight={"40em"}
          style={{ marginRight: "15px" }}
        >
          {modelList &&
            modelList.map((model, idx) => (
              <div key={idx} id={`model-detail-card`}>
                <Model
                  k={model?.id}
                  model={model}
                  isViewDeployedAll={isViewDeployedAll}
                  setModelsView={setModelsView}
                />
              </div>
            ))}
        </ReactQueryLoading>
      </CRow>
    </CContainer>
  );
};

export default ModelList;

const Model = (props) => {
  const [collapsed, setCollapsed] = useState(true);
  const [hasDeployedVersions, setHasDeployedVersions] = useState(false);

  const queryClient = useQueryClient();
  const updateModel = () => {
    queryClient.invalidateQueries("getModels");
    queryClient.invalidateQueries(["getModel", props.model.id]);
  };

  const toggleCollapse = () => {
    const isCollapsedOnDeployedView =
      props.isViewDeployedAll && collapsed && !hasDeployedVersions;

    if (isCollapsedOnDeployedView) {
      props.setModelsView("all");
    }

    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (props.isViewDeployedAll) {
      const checkIfModelHasAnyDeployedVersions = (versions) => {
        let hasDeployed = versions.some((version) => version.is_deployed);
        setCollapsed(!hasDeployed);
        setHasDeployedVersions(hasDeployed);
      };

      checkIfModelHasAnyDeployedVersions(props.model.versions);
    }
  }, [props.model.versions, props.isViewDeployedAll]);

  return (
    <CCard
      data-testid={`mdl-card-${props.model.id}`}
      style={{
        minWidth: "670px",
      }}
    >
      <CCardHeader>
        {/* https://stackoverflow.com/questions/11501025/div-anchors-scrolling-too-far */}
        <a
          style={{
            display: "block",
            position: "relative",
            top: "-60px",
            visibility: "hidden",
          }}
          id={`model-${props.model.id}`}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton
            data-testid={`mdl-btn-toggle-${props.model.id}`}
            data-attr={`models:btn:toggle-model-card-${props.model.id}`}
            className="card-header-action"
            onClick={() => toggleCollapse()}
            aria-label="Toggle model card collapse"
            style={{ marginRight: "25px", flexGrow: 0 }}
          >
            {collapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
          <div className="model-details">
            <h2 id={`model-name-header`}>{props.model.name}</h2>
            <div className="model-description">{props.model.description}</div>
            <ModelDetailsButton block={false} model={props.model} />
          </div>
        </div>
      </CCardHeader>
      {}
      {!collapsed && (
        <CCollapse show={!collapsed}>
          {props?.isViewDeployedAll && hasDeployedVersions && (
            <>
              <CCardBody>
                {/* only show deployed models */}
                <RQModelVersionsList
                  modelId={props.model.id}
                  refreshParent={updateModel}
                  isViewDeployedAll={props.isViewDeployedAll}
                />
                <h4 style={{ margin: ".75em" }}>Associated Datasets</h4>
                <ModelDatasets model_id={props.model.id} />
              </CCardBody>
              <CCardFooter>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  useFlexGap
                  spacing={1}
                  flexWrap="wrap"
                >
                  <AssociateDatasetButton
                    disabled={
                      props.model?.model_products[0]?.products?.id ===
                      ProjectAIProductID
                    }
                    hideIcon
                    executecontents="Associate New Dataset"
                    modaltitle="Associate New Dataset"
                    sx={{ flexGrow: 1 }}
                  >
                    <AssociateDatasetForm model_id={props.model.id} />
                  </AssociateDatasetButton>
                  <ModelAddNewVersionButton
                    modelId={props.model.id}
                    readyModel={props.model.ready_model}
                    style={{ flexGrow: 1 }}
                  />
                </Stack>
              </CCardFooter>
            </>
          )}
          {!props?.isViewDeployedAll && (
            <>
              <CCardBody>
                {/* only show deployed models */}
                <RQModelVersionsList
                  modelId={props.model.id}
                  refreshParent={updateModel}
                  isViewDeployedAll={props.isViewDeployedAll}
                />
                <h4 style={{ margin: ".75em" }}>Associated Datasets</h4>
                <ModelDatasets model_id={props.model.id} />
              </CCardBody>
              <CCardFooter>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  useFlexGap
                  spacing={1}
                  flexWrap="wrap"
                >
                  <AssociateDatasetButton
                    disabled={
                      props.model?.model_products[0]?.products?.id ===
                      ProjectAIProductID
                    }
                    hideIcon
                    executecontents="Associate New Dataset"
                    modaltitle="Associate New Dataset"
                    sx={{ flexGrow: 1 }}
                  >
                    <AssociateDatasetForm model_id={props.model.id} />
                  </AssociateDatasetButton>
                  <ModelAddNewVersionButton
                    modelId={props.model.id}
                    readyModel={props.model.ready_model}
                    style={{ flexGrow: 1 }}
                  />
                </Stack>
              </CCardFooter>
            </>
          )}
        </CCollapse>
      )}
    </CCard>
  );
};

Model.propTypes = {
  model: PropTypes.object.isRequired,
  isViewDeployedAll: PropTypes.bool,
  setModelsView: PropTypes.func,
};

export class ModelVersionsList extends React.Component {
  constructor(props) {
    super(props);
    this.state = { activeTab: 0 };
  }

  render() {
    return (
      <CRow>
        {this.props.versions.map(
          (version) =>
            this.props.isViewDeployedAll &&
            version.is_deployed && (
              <ModelVersion
                key={version.id}
                modelversion={version}
                model_id={this.model_id}
                model={this.props.model}
                refreshParent={this.props.refreshParent}
              />
            ),
        )}

        {this.props.versions.map(
          (version) =>
            !this.props.isViewDeployedAll && (
              <ModelVersion
                key={version.id}
                modelversion={version}
                model_id={this.model_id}
                model={this.props.model}
                refreshParent={this.props.refreshParent}
              />
            ),
        )}
      </CRow>
    );
  }
}

ModelVersionsList.propTypes = {
  versions: PropTypes.array.isRequired,
  model: PropTypes.object.isRequired,
  refreshParent: PropTypes.func.isRequired,
  isViewDeployedAll: PropTypes.bool,
};

export const RQModelVersionsList = (props) => {
  /*
   * Model details is still a class component, and it's a bit of
   * a pain to refactor.  To get other model version details to run
   * correctly, we will use react query wrapper here.
   */
  const { data: model } = useGetModel(props.modelId);
  return (
    <>
      {model && (
        <ModelVersionsList
          versions={model.versions}
          model={model}
          refreshParent={props.refreshParent}
          isViewDeployedAll={props.isViewDeployedAll}
        />
      )}
    </>
  );
};

RQModelVersionsList.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshParent: PropTypes.func.isRequired,
  isViewDeployedAll: PropTypes.bool,
};

function UseModelVersionInputFields(props) {
  // HOC component to convert react query hook
  return props.children(
    useModelVersionInputFields(
      props.model?.id,
      props.modelversion?.version_number,
    ),
  );
}

const ModelVersion = (props) => {
  const [collapsed, setCollapsed] = useState(true);
  const is_trained = ["trained", "deployed"].includes(
    props.modelversion.status_fk,
  );
  const isModelDeployed = props?.modelversion?.is_deployed;

  const setModelAccentColor = (modelVersion) => {
    if (modelVersion.status_fk === "retired") {
      return "warning";
    }

    return modelVersion.is_deployed ? "success" : "danger";
  };

  return (
    <CCol xs="12" sm="12" md="12" xxl="12">
      <CCard
        accentColor={setModelAccentColor(props.modelversion)}
        id={`model-version-card`}
        data-testid={`mdl-version-row-${props.modelversion.version_number}`}
      >
        <CCardHeader>
          {/* https://stackoverflow.com/questions/11501025/div-anchors-scrolling-too-far */}
          <a
            style={{
              display: "block",
              position: "relative",
              top: "-60px",
              visibility: "hidden",
            }}
            id={`model-${props.model.id}-version-${props.modelversion.version_number}`}
          />
          <IconButton
            className="card-header-action"
            onClick={() => setCollapsed(!collapsed)}
            style={{ marginRight: "25px", flexGrow: 0 }}
            data-testid={`mdl-version-toggle-${props.modelversion.version_number}`}
            data-attr={`models:btn:toggle-version-${props.modelversion.version_number}`}
          >
            {collapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
          Version:{" "}
          <span className="model-version-identifier">
            {props.modelversion.version_number}
          </span>
          <div className="float-right">
            <div
              id="model-deployment-status-badge"
              className="card-header-actions"
              data-testid={`mdl-version-status-${props.modelversion.version_number}`}
            >
              <ModelDeployedBadge
                is_deployed={props.modelversion.is_deployed}
                status_fk={props.modelversion.status_fk}
              />
            </div>
          </div>
        </CCardHeader>
        <CCollapse show={!collapsed}>
          <CCardBody>
            <ModelVersionConfig
              modelversion={props.modelversion}
              {...props}
              display_keys={["source_data"]}
            />
          </CCardBody>
          {/* If the model version is retired don't show the footer */}
          {props.modelversion.status_fk !== "retired" && (
            <CCardFooter>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                useFlexGap
                spacing={1}
                flexWrap="wrap"
              >
                {is_trained ? (
                  <>
                    <UseModelVersionInputFields
                      model={props.model}
                      modelversion={props.modelversion}
                    >
                      {({
                        data: modelVersionInputFields,
                        isLoading: modelVersionInputFieldsLoading,
                      }) => {
                        return (
                          <AssociateDatasetButton
                            executecontents="Map Associated Dataset"
                            modaltitle="Map Associated Dataset"
                            disabled={
                              !modelVersionInputFieldsLoading &&
                              (modelVersionInputFields?.length ?? 0) === 0
                            }
                            sx={{ flexGrow: 1 }}
                            data-testid={`mdl-version-map-dataset-${props.modelversion.version_number}`}
                          >
                            <MapDataSetFieldsForm
                              model_id={props.model.id}
                              model_version_num={
                                props.modelversion.version_number
                              }
                            />
                          </AssociateDatasetButton>
                        );
                      }}
                    </UseModelVersionInputFields>
                    <ModelInferModalButton
                      modelversion={props.modelversion}
                      model={props.model}
                      sx={{ flexGrow: 1 }}
                      disabled={!isModelDeployed}
                    />
                    <Can
                      perform="model:read"
                      yes={() =>
                        !props.model?.remote_model && (
                          <VersionLogsButton
                            model={props.model}
                            id={props.model.id}
                            versionNum={props.modelversion.version_number}
                            request={`models/${props.model.id}/version/${props.modelversion.version_number}/logs`}
                            sx={{ flexGrow: 1 }}
                          />
                        )
                      }
                    />
                    {!props.model?.remote_model && (
                      <CreateItemButton
                        itemName="Modify"
                        icon={<EditOutlined />}
                        disabled={
                          props.modelversion.is_deployed ||
                          props.model?.model_products[0]?.products?.id ===
                            ProjectAIProductID
                        }
                        createItemModal={ModifyModelVersionModal}
                        createItemModalProps={{
                          modelId: props.model.id,
                          modelVersionId: props.modelversion.version_number,
                        }}
                        style={{ flexGrow: 1 }}
                      />
                    )}
                    <ModelDeployToggleButton
                      model_id={props.model.id}
                      model_version={props.modelversion.version_number}
                      model={props.model}
                      sx={{ flexGrow: 1 }}
                    />
                  </>
                ) : (
                  <>
                    <ModelTrainButton
                      model={props.model}
                      version_number={props.modelversion.version_number}
                      status_fk={props.modelversion.status_fk}
                      onActionComplete={props.refreshParent}
                      sx={{ flexGrow: 1 }}
                    />
                  </>
                )}
              </Stack>
            </CCardFooter>
          )}
        </CCollapse>
      </CCard>
    </CCol>
  );
};

ModelVersion.propTypes = {
  modelversion: PropTypes.object.isRequired,
  model: PropTypes.object.isRequired,
  refreshParent: PropTypes.func.isRequired,
};

class ModelDeployedBadge extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    if (this.props.status_fk === "retired") {
      return (
        <CBadge style={{ verticalAlign: "middle" }} color="warning">
          Retired
        </CBadge>
      );
    } else if (this.props.is_deployed) {
      return (
        <CBadge style={{ verticalAlign: "middle" }} color="success">
          Deployed
        </CBadge>
      );
    } else if (!this.props.is_deployed) {
      return (
        <CBadge style={{ verticalAlign: "middle" }} color="danger">
          Not Deployed
        </CBadge>
      );
    }
  }
}

ModelDeployedBadge.propTypes = {
  is_deployed: PropTypes.bool.isRequired,
  status_fk: PropTypes.string.isRequired,
};

export class InferDataRender extends React.Component {
  render() {
    if (
      Array.isArray(this.props.renderData) &&
      this.props.renderData.length > 0 &&
      typeof this.props.renderData[0] === "object" &&
      this.props.renderData[0] !== null
    ) {
      const scopedSlots = {};
      let fields = Object.keys(this.props.renderData[0]);
      if (
        Object.prototype.hasOwnProperty.call(
          this.props.renderData[0],
          "confidence",
        )
      ) {
        // eslint-disable-next-line react/display-name
        scopedSlots.Confidence = (item) => (
          <td>{(100 * item.confidence).toFixed(2)}%</td>
        );
        fields = fields.filter((fieldName) => {
          return fieldName !== "confidence";
        });
        fields.push("Confidence");
      }
      if (this.props.renames) {
        // list of columns to rename if needed
        Object.entries(this.props.renames).forEach(([oldName, newName]) => {
          scopedSlots[newName] = (item) => <td>{item[oldName]}</td>;
          fields.forEach((element, index) => {
            if (element === oldName) {
              fields[index] = newName;
            }
          });
        });
      }
      return (
        <CDataTable
          items={this.props.renderData}
          fields={fields}
          scopedSlots={scopedSlots}
        />
      );
    }
    return this.props.renderData;
  }
}

InferDataRender.propTypes = {
  renderData: PropTypes.array.isRequired,
  renames: PropTypes.object,
};

const MultiOutputModelInferForm = ({
  setInferenceData,
  displayUpgrade,
  inputFields,
  value,
}) => {
  // Form to display immediate inference of models

  return (
    <>
      <div>
        {displayUpgrade && (
          <CAlert color="danger">
            No free predictions remaining this billing period, please upgrade to{" "}
            <a href="http://nuclearn.ai/#pricing_section">Cloud Plus</a> plan.
          </CAlert>
        )}
        <CForm style={{ width: "100%" }}>
          {inputFields &&
            inputFields.map((inputField) => {
              return (
                <CFormGroup row key={inputField?.id}>
                  <CLabel className="col-sm-3 col-form-label">
                    {inputField.name}
                  </CLabel>
                  <CCol sm={9}>
                    <TextareaAutosize
                      id={`update-predictnow-${inputField.name}`}
                      className="styled-textarea"
                      minRows={2}
                      maxRows={50}
                      type="text"
                      name={inputField.name}
                      onChange={(e) => {
                        setInferenceData(inputField.name, e.target.value);
                      }}
                      defaultValue={value[inputField.name]}
                      //TODO: be able to set an initial value based on some type of model config ie. for instruction, initial value provides clue like "write a cr"
                    ></TextareaAutosize>
                  </CCol>
                </CFormGroup>
              );
            })}
        </CForm>
      </div>
    </>
  );
};

MultiOutputModelInferForm.propTypes = {
  setInferenceData: PropTypes.func.isRequired,
  displayUpgrade: PropTypes.bool.isRequired,
  inputFields: PropTypes.array.isRequired,
  responseData: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  value: PropTypes.object.isRequired,
};

const ModelOutputResponseDisplay = ({ response_data }) => {
  return (
    response_data.output &&
    Object.keys(response_data.output).map((output_name, i) => {
      const predictions = response_data.output[output_name];
      return (
        <div key={i}>
          <InferDataRender
            renames={{
              label: output_name,
            }}
            renderData={predictions}
          />
        </div>
      );
    })
  );
};

export const ModelInferForm = (props) => {
  // Form to display immediate inference of models

  const [value, setValue] = useState({});
  const [responseData, setResponseData] = useState("");
  const [displayUpgrade, setDisplayUpgrade] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: modelVersion } = useGetModelVersion(
    props.model_id,
    props.model_version,
  );

  const { data: inputFields } = useModelVersionInputFields(
    props.model_id,
    props.model_version,
  );

  const setInferenceData = (field, fieldValue) => {
    setValue((prevState) => {
      prevState[field] = fieldValue;
      return prevState;
    });
  };

  const {
    mutate: inferFromModel,
    isLoading: isInferLoading,
    /*
     * isSuccess: isInferSuccess,
     * isError: isInferError,
     * reset: resetInfer,
     * error: inferError,
     */
  } = useInferFromModel(
    // Handle Successful Inference
    (response) => {
      setResponseData(response);
      setDisplayUpgrade(false);
      setErrorMessage(null);
    },
    (error) => {
      // Handle Error
      console.log(error);
      if (error && error.response && error.response.status === 409) {
        // 409 returned when free tier usage is exceed
        setDisplayUpgrade(true);
      } else {
        setErrorMessage(error.response.data.detail);
      }
    },
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    const post_data = {
      infer_data: JSON.stringify(value),
    };

    inferFromModel({
      model_id: props.model_id,
      model_version: props.model_version,
      infer_data: post_data,
    });
  };

  useEffect(() => {
    inputFields?.forEach((inputField) => (value[inputField.name] = ""));
  }, [inputFields, value]);

  if (isInferLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Loading
          height={150}
          width={150}
          color={"#9EA59D"}
          loading_message={"Talking to the model..."}
          loading={isInferLoading}
        />
      </div>
    );
  }
  let form = <></>;
  switch (modelVersion?.output_data_config?.output_type) {
    case "vector":
      form = (
        <>
          <CAlert color="danger">
            Predict now is not supported with a vector output type model.
          </CAlert>
        </>
      );
      break;
    case "text_generative":
      form = (
        <>
          <MultiOutputModelInferForm
            setInferenceData={setInferenceData}
            handleSubmit={handleSubmit}
            displayUpgrade={displayUpgrade}
            inputFields={inputFields}
            responseData={responseData}
            value={value}
          />
          <CButton
            onClick={handleSubmit}
            color="success"
            disabled={displayUpgrade}
            id="predict-now-button"
            data-attr="models:btn:predict-now"
          >
            <CIcon content={freeSet.cilBolt} /> Predict Now
            <CIcon content={freeSet.cilBolt} />
          </CButton>
          <TextGenerativeModelResponseDisplay responseData={responseData} />
        </>
      );
      break;
    default:
      form = (
        <>
          <MultiOutputModelInferForm
            setInferenceData={setInferenceData}
            handleSubmit={handleSubmit}
            displayUpgrade={displayUpgrade}
            inputFields={inputFields}
            responseData={responseData}
            value={value}
          />
          <CButton
            onClick={handleSubmit}
            color="success"
            disabled={displayUpgrade}
            id="predict-now-button"
            data-attr="models:btn:predict-now"
          >
            <CIcon content={freeSet.cilBolt} /> Predict Now
            <CIcon content={freeSet.cilBolt} />
          </CButton>
          <ModelOutputResponseDisplay response_data={responseData} />
        </>
      );
  }
  return (
    <>
      {form}
      {errorMessage && <CAlert color="danger"> {String(errorMessage)}</CAlert>}
    </>
  );
};
ModelInferForm.propTypes = {
  model_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  model_version: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

const TextGenerativeModelResponseDisplay = ({ responseData }) => {
  //TODO: refactor this next line
  const label = responseData?.output?.result?.infer_data[0]?.label[0] || "";
  if (!label) {
    return null; // Do not render anything if label is not available
  }
  return (
    <div className="response-display">
      <div className="label">{label}</div>
    </div>
  );
};

// validate props
TextGenerativeModelResponseDisplay.propTypes = {
  responseData: PropTypes.object.isRequired,
};

export const ModelVersionInputFieldDatasetTable = (props) => {
  const { data: mvdifs, isLoading: mvdifsIsLoading } =
    useAssociatedModelVersionInputFieldsWithSourceDataRecordFields(
      props.modelId,
      props.versionNum,
      props.datasetId,
    );

  return (
    <LoadingOverlay
      active={mvdifsIsLoading}
      spinner={<Loading height={"75em"} width={"75em"} />}
      text="Loading..."
    >
      <Table size="sm" style={{ maxWidth: "100%", tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ width: "32.5%", textAlign: "right" }}>
              Model Input Field
            </th>
            <th style={{ textAlign: "center" }}> is associated to </th>
            <th style={{ textAlign: "left" }}>Data Field</th>
            {/* <th style={{ textAlign: "left" }}>Remove Field Association</th> */}
          </tr>
        </thead>
        <tbody>
          {mvdifs?.map((mvdif, idx) => {
            return (
              <tr key={idx}>
                <td style={{ width: "20%", textAlign: "right" }}>
                  {mvdif.MLModelVersionInputField.field_name}
                </td>
                <td style={{ width: "10%", textAlign: "center" }}>{"<->"}</td>
                <td style={{ width: "32.5%", textAlign: "left" }}>
                  {mvdif.SourceDataRecordField &&
                  mvdif.SourceDataRecordField.field_name
                    ? mvdif.SourceDataRecordField.field_name
                    : "(calculated field)"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </LoadingOverlay>
  );
};

ModelVersionInputFieldDatasetTable.propTypes = {
  modelId: PropTypes.number.isRequired,
  versionNum: PropTypes.number.isRequired,
  datasetId: PropTypes.number.isRequired,
};

export const InputFieldDatasetMapModal = (props) => {
  const onClose = () => props.onClose();

  const deleteModelVersionDatasetAssociation =
    useDeleteModelVersionDatasetAssociation();

  const handleDelete = async (event) => {
    event.preventDefault();
    try {
      await deleteModelVersionDatasetAssociation.mutateAsync({
        modelId: props.model.id,
        modelVersionNum: props.modelversion.version_number,
        datasetId: props.dataset.id,
      });
      onClose();
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      alert(
        "There was an error deleting the modelversion<-> dataset association",
      );
    }
  };

  return (
    <CModal show={props.isVisible} onClose={() => onClose()}>
      <CModalHeader>
        Dataset: {props.dataset.name}, Model: {props.model.name}, Version:{" "}
        {props.modelversion.version_number}
        <CButton
          id="create-inputfielddatasetmapmodal-close-button"
          data-attr="models:btn:close-dataset-map-modal"
          color="secondary"
          onClick={() => onClose()}
          size="sm"
        >
          <CIcon name="cil-x" />
        </CButton>{" "}
      </CModalHeader>

      <CModalBody>
        <ModelVersionInputFieldDatasetTable
          modelId={props.model.id}
          versionNum={props.modelversion.version_number}
          datasetId={props.dataset.id}
        />
      </CModalBody>
      <CModalFooter>
        <CButton
          color="danger"
          onClick={handleDelete}
          data-attr="models:btn:delete-dataset-association"
        >
          Delete Dataset Association
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

InputFieldDatasetMapModal.propTypes = {
  model: PropTypes.object.isRequired,
  modelversion: PropTypes.object.isRequired,
  dataset: PropTypes.object.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const ClickableElementModal = (props) => {
  /*
   * bind a clickable element with a modal.
   * Prevents dragging a modal around with clickable element
   * modal must expose the 'isVisible' and 'onClose' props
   * clickable must respond to the onClick event
   */
  const [showModal, setShowModal] = useState(false);
  const handleClick = () => {
    setShowModal(true);
  };
  const handleClose = () => {
    setShowModal(false);
  };
  return (
    <>
      {showModal
        ? React.cloneElement(props.modal, {
            isVisible: showModal,
            onClose: handleClose,
          })
        : React.cloneElement(props.clickable, { onClick: handleClick })}
    </>
  );
};

ClickableElementModal.propTypes = {
  modal: PropTypes.element.isRequired,
  clickable: PropTypes.element.isRequired,
};

export const AssociateDatasetForm = ({ model_id, closeModal }) => {
  //Allow user to associate a dataset to an mlmodel
  const [datasetId, setDatasetId] = useState("");
  const {
    mutate: addAssociation,
    /*
     * isLoading: isAddAssociationLoading,
     * status: addAssociationStatus,
     * isError: isAddAssociationError,
     * error: AddAssociationError,
     * reset: resetAddAssociation,
     */
  } = useAssociateNewModelDataset();

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await addAssociation({ model_id, datasetId });
    } catch (error) {
      console.log(error);
    }
    closeModal();
  };

  return (
    <>
      <form onSubmit={handleSubmit} id="dataset-associate-selection-form">
        <DatasetListSelect
          handleSelectedDataset={setDatasetId}
          modelId={model_id}
        />
        <p></p>
        <CButton
          type="submit"
          color="success"
          className="dataset-associate-select-form-submit"
          data-attr="models:btn:associate-dataset"
        >
          Associate
        </CButton>
      </form>
    </>
  );
};

AssociateDatasetForm.propTypes = {
  model_id: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired,
};

AssociateDatasetForm.defaultProps = {
  closeModal: () => {},
};

export const MapDataSetFieldsForm = ({
  model_id,
  model_version_num,
  closeModal,
  defaultDatasetId,
  setStatus,
  disabled,
}) => {
  const [logInfer, setLogInfer] = useState(false);
  const [limitResults, setLimitResults] = useState("aboveprob");
  const [resultsLimitedTo, setResultsLimitedTo] = useState(1.0);
  const [datasetId, setDatasetId] = useState("");
  const [allInputFieldsMatch, setAllInputFieldsMatch] = useState(false);
  const [jsonParsingErrors, setJsonParsingErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modelVersionInputFieldsMap, setModelVersionInputFieldsMap] = useState(
    {},
  );

  useEffect(() => {
    setDatasetId(defaultDatasetId || "");
  }, [defaultDatasetId]);

  const { data: modelVersionDataset, isLoading: modelVersionDatasetIsLoading } =
    useModelVersionDataset(model_id, model_version_num, datasetId);

  const { data: modelDatasets, isLoading: modelDatasetsIsLoading } =
    useModelDatasets(model_id, model_version_num);

  const { data: modelVersionInputFields } = useModelVersionInputFields(
    model_id,
    model_version_num,
  );

  const { data: datasetFields, isLoading: datasetFieldsIsLoading } =
    useDatasetFields(datasetId);

  const addModelVersionDatasetTie = useAssociateNewModelVersionDataset();

  const {
    mutate: updateAssociatedModelVersionInputFields,
    isSuccess: updateAssociatedIsSuccess,
    error: updateAssociatedError,
  } = useAssociateModelVersionInputFieldsWithModelVersionDataset();

  const { data: backendModelVersionInputFieldsWithSourceDataRecordFields } =
    useAssociatedModelVersionInputFieldsWithSourceDataRecordFields(
      model_id,
      model_version_num,
      datasetId,
    );
  console.log(
    "backendModelVersionInputFieldsWithSourceDataRecordFields",
    backendModelVersionInputFieldsWithSourceDataRecordFields,
  );
  function calculateAllInputFieldsMatch(newModelVersionInputFieldsMap) {
    let isFilledOut = true;
    // set to null if any field isn't filled out
    if (!modelVersionInputFields) {
      isFilledOut = false;
    } else {
      modelVersionInputFields.map((mvif) => {
        if (mvif.id in newModelVersionInputFieldsMap) {
          const newMvif = newModelVersionInputFieldsMap[mvif.id];
          if (
            newMvif?.source_data_record_field_id === "" ||
            (newMvif?.source_data_record_field_id === null &&
              newMvif?.calculated_field_json === null)
          ) {
            isFilledOut = false;
          }
        } else {
          isFilledOut = false;
        }
      });
    }
    setAllInputFieldsMatch(isFilledOut);
  }

  useEffect(() => {
    if (modelVersionDataset) {
      setLogInfer(modelVersionDataset?.log_inference_to_dataset);
      setLimitResults(
        modelVersionDataset?.limit_stored_predictions?.limit_results,
      );
      setResultsLimitedTo(
        modelVersionDataset?.limit_stored_predictions?.limit_results ===
          "aboveprob"
          ? modelVersionDataset?.limit_stored_predictions?.results_limited_to *
              100.0
          : modelVersionDataset?.limit_stored_predictions?.results_limited_to,
      );
    }

    // Process the backend data to build the mapping
    let newModelVersionInputFieldsMap = {};

    if (backendModelVersionInputFieldsWithSourceDataRecordFields?.length > 0) {
      // Process existing mappings from the backend
      backendModelVersionInputFieldsWithSourceDataRecordFields.forEach(
        (mvdif) => {
          const inputFieldId = mvdif?.MLModelVersionInputField?.id;
          const sourceDataFieldId = mvdif?.SourceDataRecordField?.id || null;
          const calculatedFieldJson =
            mvdif?.MLModelVersionDatasetInputSourceDataRecordField
              ?.calculated_field_json;

          if (inputFieldId) {
            newModelVersionInputFieldsMap[inputFieldId] = {
              source_data_record_field_id: sourceDataFieldId,
              calculated_field_json: calculatedFieldJson
                ? JSON.stringify(calculatedFieldJson, null, 2)
                : null,
            };
          }
        },
      );
    }

    // Add any model input fields that don't have mappings yet
    if (modelVersionInputFields?.length > 0) {
      modelVersionInputFields.forEach((mvif) => {
        if (!newModelVersionInputFieldsMap[mvif.id]) {
          // Check if there is an exactly matching source data record input field
          const matchingDatasetFields = datasetFields?.filter((df) => {
            return df?.field_name?.toLowerCase() === mvif?.name?.toLowerCase();
          });

          newModelVersionInputFieldsMap[mvif.id] = {
            source_data_record_field_id:
              matchingDatasetFields?.length === 1
                ? matchingDatasetFields[0]?.id
                : "",
            calculated_field_json: null,
          };
        }
      });
    }

    setModelVersionInputFieldsMap(newModelVersionInputFieldsMap);
    calculateAllInputFieldsMatch(newModelVersionInputFieldsMap);
  }, [
    modelVersionDataset,
    modelVersionInputFields,
    datasetFields,
    backendModelVersionInputFieldsWithSourceDataRecordFields,
  ]);

  async function handleSubmitFieldMappings(event, inputFieldMap, logInfer) {
    event.preventDefault();
    // Deep copy object.  If we don't when we parse the field json and update it will update the inputFieldMap state
    let submitInputFieldMap = JSON.parse(JSON.stringify(inputFieldMap));
    // verify the json, log errors in newErrors object
    let newErrors = [];
    Object.keys(inputFieldMap).forEach((key) => {
      const calculatedFieldJson = inputFieldMap[key].calculated_field_json;
      if (calculatedFieldJson !== null) {
        try {
          submitInputFieldMap[key].calculated_field_json =
            JSON.parse(calculatedFieldJson);
        } catch (error) {
          newErrors.push(error);
        }
      }
    });
    setJsonParsingErrors(newErrors);
    if (newErrors.length > 0) {
      // If there are any errors, just return without trying to save anything to the backend
      return false;
    }

    setLoading(true);

    let modelVersionDatasetTie = await addModelVersionDatasetTie.mutateAsync({
      model_id,
      model_version_num,
      datasetId,
      logInfer,
      limit_results: limitResults,
      results_limited_to:
        limitResults === "aboveprob"
          ? resultsLimitedTo / 100.0
          : resultsLimitedTo,
    });

    let modelVersionDatasetTieId = modelVersionDatasetTie.data.id;

    updateAssociatedModelVersionInputFields({
      model_id,
      model_version_num,
      modelVersionDatasetTieId,
      inputFieldMap: submitInputFieldMap,
    });

    if (setStatus) {
      setStatus(modelVersionDatasetTie && modelVersionDatasetTie.status);
    }
    return true;
  }

  // handle errors with update
  useEffect(() => {
    if (updateAssociatedIsSuccess) {
      setLoading(false);
      closeModal();
    }
    if (updateAssociatedError) {
      setLoading(false);
    }
  }, [updateAssociatedIsSuccess, updateAssociatedError]);

  // only works for a single update, if updating state multiple times in a single render use the multipleSelectionPair function
  function saveSingleSelectionPair(
    modelVersionInputFieldId,
    datasetFieldId,
    calculatedFieldJson,
  ) {
    const newModelVersionInputFieldsMap = {
      ...modelVersionInputFieldsMap,
      [modelVersionInputFieldId]: {
        // use value as key in object literal, see https://stackoverflow.com/questions/2274242/how-to-use-a-variable-for-a-key-in-a-javascript-object-literal
        source_data_record_field_id: datasetFieldId,
        calculated_field_json:
          datasetFieldId === null ? calculatedFieldJson : null,
      },
    };
    // If a specific sourceDataRecord id is set, use that
    setModelVersionInputFieldsMap(newModelVersionInputFieldsMap);
    calculateAllInputFieldsMatch(newModelVersionInputFieldsMap);
  }
  // Need a function to save multiple selection pairs.  The single selection pair function won't update state correctly if
  // called multiple times in one update

  return (
    <LoadingOverlay
      active={loading}
      spinner={<Loading height={"75em"} width={"75em"} />}
      text="Saving Field Mappings"
    >
      <div>
        {modelVersionInputFields?.length === 0 ? (
          <CAlert color="danger" style={{ width: "100%" }}>
            This model does not have any input fields defined, ensure input
            fields were defined during model creation.
          </CAlert>
        ) : (
          <CRow style={{ justifyContent: "center", margin: ".75em" }}>
            <CCol md="12">
              <h5 style={{ margin: ".75em" }}>
                Select a Dataset To Get Started:
              </h5>
              {modelDatasetsIsLoading ? (
                <>Loading...</>
              ) : (
                <ListSelect
                  handleSelectedValue={setDatasetId}
                  items={modelDatasets}
                  selectedValue={datasetId}
                  disabled={disabled}
                  selectDomID="modelVersionDatasetSelect"
                  data-testid="mdl-map-dataset-select"
                />
              )}
            </CCol>
          </CRow>
        )}

        <form
          onSubmit={(event) =>
            handleSubmitFieldMappings(
              event,
              modelVersionInputFieldsMap,
              logInfer,
            )
          }
        >
          {datasetId ? (
            <>
              <CFormGroup row>
                <CCol md="4">
                  <CLabel
                    htmlFor="aggregation-select"
                    className="col-form-label"
                  >
                    Log All Model Infer Calls made to this dataset?
                  </CLabel>
                </CCol>
                <CCol xs="12" md="8">
                  {!modelVersionDatasetIsLoading && (
                    <CSwitch
                      name="dataset-infer-log-switch"
                      id="dataset-infer-log-switch"
                      labelOn="Yes"
                      onChange={(e) => {
                        setLogInfer(e.target.checked);
                      }}
                      checked={logInfer}
                      size="lg"
                      labelOff="No"
                      color="success"
                      style={{ textAlign: "left" }}
                    ></CSwitch>
                  )}
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CCol md="4">
                  <CLabel htmlFor="limit-select" className="col-form-label">
                    Limit/prune stored predictions?
                  </CLabel>
                </CCol>
                <CCol xs="12" md="8">
                  {!modelVersionDatasetIsLoading && (
                    <ScoreModelParamsLimit
                      limitResults={limitResults}
                      resultsLimitedTo={resultsLimitedTo}
                      onLimitResultsChange={setLimitResults}
                      onResultsLimitedToChange={setResultsLimitedTo}
                      defaultTopNResultsLimitedTo={50}
                      defaultAboveProbResultsLimitedTo={1.0}
                    />
                  )}
                </CCol>
              </CFormGroup>
            </>
          ) : null}
          <CRow
            style={{
              margin: "25px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {jsonParsingErrors.length > 0 && (
              <CRow style={{ marginBottom: ".75em" }}>
                <CCol md="12">
                  {jsonParsingErrors.map((error, idx) => {
                    return (
                      <CAlert color="danger" key={idx}>
                        Error parsing calculated field json: {error?.message}
                      </CAlert>
                    );
                  })}
                </CCol>
              </CRow>
            )}
            {updateAssociatedError && (
              <CRow style={{ marginBottom: ".75em" }}>
                <CCol md="12">
                  <CAlert color="danger">
                    Error saving: {updateAssociatedError?.message}
                    <br />
                    {updateAssociatedError?.response?.data?.detail}
                  </CAlert>
                </CCol>
              </CRow>
            )}
            {datasetId && (
              <CRow style={{ marginBottom: ".75em" }}>
                <CCol md="4" style={{ textAlign: "right" }}>
                  <h5>Model Input Field</h5>
                </CCol>
                <CCol md="8">
                  <h5>Data Field</h5>
                </CCol>
              </CRow>
            )}

            {datasetId &&
            Object.keys(modelVersionInputFieldsMap).length > 0 &&
            modelVersionInputFields?.length > 0
              ? modelVersionInputFields.map((modelVersionInputField, idx) => {
                  /*
                   * let fieldPair = mvdifs.filter(
                   *   (mvdif) =>
                   *     mvdif.MLModelVersionInputField?.id ==
                   *     modelVersionInputField?.id
                   * );
                   * const sdrfId =
                   *   fieldPair.length > 0
                   *     ? fieldPair[0].SourceDataRecordField?.id
                   *     : null;
                   * const calculatedFieldJson =
                   *   fieldPair.length > 0
                   *     ? fieldPair[0]
                   *         .MLModelVersionDatasetInputSourceDataRecordField
                   *         ?.calculated_field_json
                   *     : null;
                   * const fieldPairData = {
                   *   sourceDataRecordFieldId: sdrfId,
                   *   calculatedFieldJson: calculatedFieldJson,
                   * };
                   */
                  const currentModelVersionInput =
                    modelVersionInputFieldsMap[modelVersionInputField?.id];

                  return (
                    <CRow key={idx}>
                      <CCol md="4" style={{ textAlign: "right" }}>
                        <div>
                          <span className="model-input-field-name">
                            {modelVersionInputField.name}
                          </span>{" "}
                          {modelVersionInputField.description}
                        </div>
                      </CCol>
                      <CCol md="8">
                        {datasetFieldsIsLoading ? (
                          <>Loading...</>
                        ) : (
                          <>
                            <ListSelect
                              handleSelectedValue={(selectedValue) => {
                                // if the user selects the calculated field, set the value to null
                                saveSingleSelectionPair(
                                  modelVersionInputField?.id,
                                  selectedValue === "-1" ? null : selectedValue,
                                  null,
                                );
                              }}
                              items={[
                                {
                                  id: -1,
                                  field_name: "Calculated Field",
                                  field_description:
                                    "Field for custom JSON payload",
                                },
                                ...datasetFields,
                              ]}
                              selectDomID={modelVersionInputField?.id}
                              itemNameKeyIs="field_name"
                              data-testid={`mdl-map-field-select-${modelVersionInputField.name}`}
                              selectedValue={
                                // take the id if exists else set -1
                                currentModelVersionInput?.source_data_record_field_id ===
                                null
                                  ? "-1"
                                  : currentModelVersionInput?.source_data_record_field_id
                              }
                            />
                            {currentModelVersionInput?.source_data_record_field_id ===
                              null && (
                              <CTextarea
                                id="update-new-automation-template-rawdata-textarea"
                                type="text"
                                style={{
                                  fontSize: "85%",
                                  fontFamily: "monospace",
                                  backgroundColor: "#f6f6f6",
                                  marginTop: "3%",
                                  marginBottom: "3%",
                                }}
                                spellCheck="false"
                                rows="10"
                                name="rawdata"
                                placeholder="Raw Data (JSON)"
                                value={
                                  currentModelVersionInput?.calculated_field_json
                                }
                                onChange={(e) => {
                                  saveSingleSelectionPair(
                                    modelVersionInputField?.id,
                                    null,
                                    e.target.value,
                                  );
                                }}
                                disabled={false}
                              />
                            )}
                          </>
                        )}
                      </CCol>
                    </CRow>
                  );
                })
              : null}
          </CRow>
          <p></p>

          <CButton
            type="submit"
            color="success"
            disabled={!allInputFieldsMatch}
            data-testid="mdl-map-dataset-save"
          >
            Save Changes
          </CButton>
        </form>
      </div>
    </LoadingOverlay>
  );
};

MapDataSetFieldsForm.propTypes = {
  model_id: PropTypes.number,
  model_version_num: PropTypes.number.isRequired,
  closeModal: PropTypes.func.isRequired,
  defaultDatasetId: PropTypes.number,
  setStatus: PropTypes.func,
  disabled: PropTypes.bool,
};

MapDataSetFieldsForm.defaultProps = {
  closeModal: () => {}, // prevent failure as close is a cloned prop see https://github.com/facebook/react/issues/6653#issuecomment-215793856
};
